import type { MetricFormat } from '../../../types/manualDashboard';
import type { LookupMeasureFormat, LookupRecordFormat } from '../../../types/lookupDashboard';

/**
 * A measure's format as the board's figure formatter takes it. `date`
 * measures only ever appear inside notes, written by the server, so one
 * reaching a figure is drawn as a plain number rather than failing.
 */
export function measureFormat(format: LookupMeasureFormat): MetricFormat {
  return format === 'date' ? 'int' : format;
}

/** One record cell, as the table and the chat both show it. */
export function formatRecordValue(value: unknown, format: LookupRecordFormat): string {
  if (value === null || value === undefined || value === '') return '—';
  switch (format) {
    case 'money2': {
      const n = Number(value);
      return Number.isFinite(n)
        ? `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : String(value);
    }
    case 'money0': {
      const n = Number(value);
      return Number.isFinite(n) ? `$${Math.round(n).toLocaleString('en-US')}` : String(value);
    }
    case 'int': {
      const n = Number(value);
      return Number.isFinite(n) ? Math.round(n).toLocaleString('en-US') : String(value);
    }
    case 'date':
      return String(value).slice(0, 10);
    case 'text':
    default:
      return String(value);
  }
}

/** What a record sorts by: numbers as numbers, ISO dates and text as themselves. */
export function recordSortValue(format: LookupRecordFormat) {
  return (value: unknown): string | number | null => {
    if (value === null || value === undefined || value === '') return null;
    if (format === 'money0' || format === 'money2' || format === 'int') {
      const n = Number(value);
      return Number.isFinite(n) ? n : null;
    }
    return String(value);
  };
}

/** "13 legs", "1 leg" — for a count beside a suggestion. */
export function countLabel(count: number, noun: string): string {
  return `${count.toLocaleString('en-US')} ${noun}${count === 1 ? '' : 's'}`;
}
